import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useStore } from '../lib/store.js';
import { DEFAULT_WIPE_EXTRAS } from '@panel/shared';
import CorePicker from '../components/CorePicker.js';

interface Machine { machine_name: string; online: boolean; server_count: number }

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const GROUPS = ['1x', '2x', '3x', '5x', '10x'];

export default function NewServer() {
  const nav = useNavigate();
  const [machines, setMachines] = useState<Machine[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const machineTelemetry = useStore((s) => s.machines);

  const [form, setForm] = useState({
    Server_Name: '',
    Server_Org: '',
    Server_IP: '',
    Server_rcon_port: '28016',
    Server_rcon_password: '',
    Server_Group: '2x',
    Server_AFFINITY: '',
    Server_map: '',
    Server_Directory: '',
    Server_Machine: '',
    Auto_Wipe: false,
    wipe_days: [4] as number[],
    wipe_time: '14:00',
    Wipe_Extras: DEFAULT_WIPE_EXTRAS.join('\n'),
    Pop_Bot_Enabled: false,
    Pop_Bot_Token: '',
    Pop_Bot_Status_Template: '({playersOnline}/{maxPlayers}) Online',
    Pop_Bot_Offline_Status: '[ OFFLINE ]',
  });

  useEffect(() => {
    api.get<{ machines: Machine[] }>('/api/machines').then((r) => {
      setMachines(r.machines);
      if (r.machines.length > 0 && !form.Server_Machine) {
        setForm((f) => ({ ...f, Server_Machine: r.machines[0].machine_name }));
      }
    }).catch((err) => setError(err.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body = {
        ...form,
        Wipe_Extras: form.Wipe_Extras.split('\n').map((s) => s.trim()).filter(Boolean),
      };
      const created = await api.post<{ Server_ID: number }>('/api/servers', body);
      nav(`/servers/${created.Server_ID}`);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-3xl space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Add New Server</h1>
        <p className="text-fg-muted text-sm mt-1 max-w-2xl">
          The Hub will write a default <code className="font-mono text-xs bg-bg-raised border border-bg-border px-1 py-0.5 rounded">startserver.bat</code> on the
          target machine if one doesn't exist. After creating, paste rustmaps URLs into the Console to populate the queue.
        </p>
      </header>

      {error && (
        <div className="card !border-status-error/40 !bg-status-error/5 px-4 py-3 text-sm text-status-error">
          {error}
        </div>
      )}

      <Section title="Identity">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Server Name *" value={form.Server_Name} onChange={(v) => set('Server_Name', v)} placeholder="EU 2x Monthly" />
          <Field label="Organization" value={form.Server_Org} onChange={(v) => set('Server_Org', v)} placeholder="optional" />
          <Field label="Outward IP *" value={form.Server_IP} onChange={(v) => set('Server_IP', v)} placeholder="1.2.3.4" />
          <SelectField label="Server Group" value={form.Server_Group} onChange={(v) => set('Server_Group', v)} options={GROUPS} />
        </div>
      </Section>

      <Section title="RCON">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="RCON Port *" value={form.Server_rcon_port} onChange={(v) => set('Server_rcon_port', v)} placeholder="28016" />
          <Field label="RCON Password *" value={form.Server_rcon_password} onChange={(v) => set('Server_rcon_password', v)} type="password" />
        </div>
      </Section>

      <Section title="Machine & Resources">
        <div>
          <label className="label">Target Machine *</label>
          <select className="input" value={form.Server_Machine} onChange={(e) => set('Server_Machine', e.target.value)} required>
            {machines.map((m) => (
              <option key={m.machine_name} value={m.machine_name}>
                {m.machine_name} {m.online ? '· online' : '· OFFLINE'} · {m.server_count} server{m.server_count === 1 ? '' : 's'}
              </option>
            ))}
          </select>
          {machines.length === 0 && (
            <p className="text-xs text-status-warn mt-1.5">
              No machines registered yet. Add one in Settings → Machines first.
            </p>
          )}
        </div>
        <div>
          <label className="label">CPU Affinity</label>
          <CorePicker
            cores={machineTelemetry.get(form.Server_Machine)?.cpu.cores}
            value={form.Server_AFFINITY}
            onChange={(v) => set('Server_AFFINITY', v)}
          />
        </div>
        <div>
          <Field
            label="Server Directory *"
            value={form.Server_Directory}
            onChange={(v) => set('Server_Directory', v)}
            placeholder="C:\\Rust\\EU_2x_Monthly"
          />
        </div>
        <Field
          label="Starting Map URL"
          value={form.Server_map}
          onChange={(v) => set('Server_map', v)}
          placeholder="https://maps.rustmaps.com/..."
          hint="optional — you can paste maps later in the Console"
        />
      </Section>

      <Section title="Auto-Wipe" header={
        <label className="flex items-center gap-2 text-sm font-normal cursor-pointer">
          <input type="checkbox" checked={form.Auto_Wipe} onChange={(e) => set('Auto_Wipe', e.target.checked)} className="accent-accent w-4 h-4 cursor-pointer" />
          Enable scheduled auto-wipes
        </label>
      }>
        {form.Auto_Wipe && (
          <>
            <div>
              <label className="label">Days</label>
              <div className="flex flex-wrap gap-1.5">
                {DAYS.map((d, i) => {
                  const sel = form.wipe_days.includes(i);
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => set('wipe_days', sel ? form.wipe_days.filter((x) => x !== i) : [...form.wipe_days, i])}
                      className={`px-3 h-8 rounded-md text-xs font-medium border cursor-pointer transition-colors ${
                        sel ? 'bg-accent/15 text-accent border-accent/60' : 'bg-bg-raised border-bg-border text-fg-muted hover:text-fg hover:border-bg-ring'
                      }`}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Time (server timezone)" value={form.wipe_time} onChange={(v) => set('wipe_time', v)} type="time" />
              <div>
                <div className="label">Force-Wipe Day</div>
                <div className="text-xs text-fg-muted bg-bg-raised border border-bg-border rounded-lg px-3 py-2 leading-relaxed">
                  Auto-skipped on first Thursday of the month.
                </div>
              </div>
            </div>
            <div>
              <label className="label">Wipe Extras (one path per line, relative to server directory)</label>
              <textarea
                className="input font-mono text-xs"
                rows={5}
                value={form.Wipe_Extras}
                onChange={(e) => set('Wipe_Extras', e.target.value)}
              />
            </div>
          </>
        )}
      </Section>

      <Section title="Discord Pop Bot" header={
        <label className="flex items-center gap-2 text-sm font-normal cursor-pointer">
          <input
            type="checkbox"
            checked={form.Pop_Bot_Enabled}
            onChange={(e) => set('Pop_Bot_Enabled', e.target.checked)}
            className="accent-accent w-4 h-4 cursor-pointer"
          />
          Enable status bot
        </label>
      }>
        {form.Pop_Bot_Enabled ? (
          <>
            <Field
              label="Bot Token *"
              value={form.Pop_Bot_Token}
              onChange={(v) => set('Pop_Bot_Token', v)}
              placeholder="Discord bot token"
              type="password"
              hint={<>From the <a href="https://discord.com/developers/applications" target="_blank" rel="noreferrer" className="text-accent hover:underline">Discord Developer Portal</a>. The bot only needs to be in your guild.</>}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                label="Status template"
                value={form.Pop_Bot_Status_Template}
                onChange={(v) => set('Pop_Bot_Status_Template', v)}
                placeholder="({playersOnline}/{maxPlayers}) Online"
                hint={<>Placeholders: <code className="bg-bg-raised border border-bg-border px-1 rounded">{'{playersOnline}'}</code> <code className="bg-bg-raised border border-bg-border px-1 rounded">{'{maxPlayers}'}</code> <code className="bg-bg-raised border border-bg-border px-1 rounded">{'{joiningPlayers}'}</code> <code className="bg-bg-raised border border-bg-border px-1 rounded">{'{queuedPlayers}'}</code></>}
              />
              <Field
                label="Offline status"
                value={form.Pop_Bot_Offline_Status}
                onChange={(v) => set('Pop_Bot_Offline_Status', v)}
                placeholder="[ OFFLINE ]"
              />
            </div>
          </>
        ) : (
          <p className="text-xs text-fg-dim">Toggle on to attach a Discord status bot to this server.</p>
        )}
      </Section>

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" className="btn-ghost" onClick={() => nav('/')}>Cancel</button>
        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? 'Creating…' : 'Create Server'}
        </button>
      </div>
    </form>
  );
}

function Section({ title, header, children }: { title: string; header?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="card overflow-hidden">
      <div className="px-5 h-12 border-b border-bg-border bg-bg-raised/30 flex items-center justify-between gap-3">
        <h2 className="font-semibold text-sm text-fg">{title}</h2>
        {header}
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </section>
  );
}

function Field({ label, value, onChange, placeholder, type = 'text', hint }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string; hint?: React.ReactNode;
}) {
  return (
    <div>
      <label className="label">{label}</label>
      <input className="input" type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      {hint && <div className="text-xs text-fg-dim mt-1.5">{hint}</div>}
    </div>
  );
}

function SelectField({ label, value, onChange, options }: {
  label: string; value: string; onChange: (v: string) => void; options: string[];
}) {
  return (
    <div>
      <label className="label">{label}</label>
      <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
